import React from "react";
import { useTranslation } from "react-i18next";
import { useBranch } from "~/contexts/BranchContext";
import { getItemName } from "~/utils/menuItemHelper";

interface CrossSellingSectionProps {
  suggestions?: {
    name: string;
    price: number;
    item_id: string;
    image_url: string;
  }[];
}

const CrossSellingSection: React.FC<CrossSellingSectionProps> = ({
  suggestions,
}) => {
  const { t, i18n } = useTranslation();
  const { theme, setSelectedItem, getConvertedPrice, menuItems } = useBranch();

  if (!suggestions || suggestions.length === 0) {
    return null;
  }

  const handleItemClick = (suggestion: { item_id: string }) => {
    // Find the full menu item to set it as selected
    const fullItem = menuItems?.find(item => item.id === suggestion.item_id);
    if (fullItem) {
      setSelectedItem(fullItem);
    }
  };

  return (
    <div className="py-4 border-t border-gray-100 mt-4">
      <h3 className="text-sm font-semibold mb-3 px-1" style={{ color: theme?.textColor }}>
        {t("menu.youMightAlsoLike", "You might also like")}
      </h3>
      
      {/* Horizontal Scroll Container - "Small line and auto hide" via scrollbar-thin class */}
      <div className="overflow-x-auto pb-2 -mx-4 px-4 scrollbar-thin flex gap-3 snap-x snap-mandatory">
        {suggestions.map((item) => {
           // Resolve full item to get localized name
           const fullItem = menuItems?.find((i) => i.id === item.item_id);
           const displayName = fullItem ? getItemName(fullItem, i18n.language) : item.name;

           return (
          <div 
            key={item.item_id} 
            className="w-[120px] flex-shrink-0 snap-center cursor-pointer group"
            onClick={() => handleItemClick(item)}
          >
            {/* Mini Card Design */}
            <div 
              className="rounded-lg overflow-hidden shadow-sm border border-gray-100 bg-white"
               style={{
                  borderColor: theme?.cardStyle === 'bordered' ? theme.borderColor : 'transparent',
                  borderRadius: theme?.borderRadius
               }}
            >
              <div className="h-[100px] w-full overflow-hidden relative">
                 <img 
                    src={item.image_url || '/images/no-image-available.jpg'} 
                    alt={displayName}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                 />
                 {/* Mini Add Button Overlay */}
                 <div className="absolute bottom-1 right-1 bg-white/90 backdrop-blur-sm rounded-full p-1 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                 </div>
              </div>
              
              <div className="p-2">
                <h4 className="text-xs font-medium text-gray-800 line-clamp-1 mb-1 leading-tight">
                  {displayName}
                </h4>
                <p 
                  className="text-xs font-bold"
                  style={{ color: theme?.primaryColor }}
                >
                  {getConvertedPrice(item.price)}
                </p>
              </div>
            </div>
          </div>
        )})}
      </div>
    </div>
  );
};

export default CrossSellingSection;
